/* eslint-disable react/prop-types */
"use client";

import React from "react";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";

const NOOP = () => {};

const parseMoney = (value) => {
  const num =
    typeof value === "string"
      ? parseFloat(value)
      : typeof value === "number"
        ? value
        : null;
  return Number.isFinite(num) ? num : null;
};

export const hasPaidParticipantReservation = (ticketState) => {
  if (!ticketState) return false;
  const totalPaid = parseMoney(ticketState.total_paid_amount);
  return Boolean(
    (Number.isFinite(totalPaid) && totalPaid > 0) ||
      ticketState.cancellation_enabled
  );
};

const formatMoney = (value, currency = "EUR") => {
  const num = parseMoney(value);
  const symbol = String(currency).toUpperCase() === "RUB" ? "₽" : "€";
  return Number.isFinite(num) ? `${num.toFixed(2)} ${symbol}` : `0.00 ${symbol}`;
};

const formatDateTime = (value, options = {}) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString(undefined, {
      day: "2-digit",
      month: "2-digit",
      year: options.includeYear === false ? undefined : "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const normalizeStatus = (value) => String(value || "").trim().toLowerCase();

const getStatusCopy = (ticketState, t) => {
  if (!ticketState) return null;
  if (ticketState.fully_paid) {
    return {
      label: t("events.details.ticket_panel.status_fully_paid"),
      className: "bg-white/12 text-white",
      summary: t("events.details.ticket_panel.summary_fully_paid"),
    };
  }
  if (ticketState.deposit_paid) {
    if (ticketState.balance_due_now) {
      return {
        label: t("events.details.ticket_panel.status_balance_due"),
        className: "bg-blue-500 text-white",
        summary: t("events.details.ticket_panel.summary_balance_due", {
          remaining: formatMoney(ticketState.outstanding_amount, ticketState.currency),
        }),
      };
    }
    return {
      label: t("events.details.ticket_panel.status_deposit_paid"),
      className: "bg-white/12 text-white",
      summary: t("events.details.ticket_panel.summary_deposit_paid", {
        remaining: formatMoney(ticketState.outstanding_amount, ticketState.currency),
      }),
    };
  }
  return {
    label: t("events.details.ticket_panel.status_payment_required"),
    className: "bg-blue-500 text-white",
    summary: t("events.details.ticket_panel.summary_payment_required"),
  };
};

const getPolicyCopy = (t, rawPolicy) => {
  const policy = normalizeStatus(rawPolicy) || "moderate";
  const knownPolicy =
    policy === "flexible" ||
    policy === "moderate" ||
    policy === "strict" ||
    policy === "vaultgrid_team_standard"
      ? policy
      : "moderate";
  return {
    label: t(`events.details.ticket_panel.policy_details.${knownPolicy}.label`),
    summary: t(`events.details.ticket_panel.policy_details.${knownPolicy}.summary`),
    rules: [
      t(`events.details.ticket_panel.policy_details.${knownPolicy}.rule_1`),
      t(`events.details.ticket_panel.policy_details.${knownPolicy}.rule_2`, {
        defaultValue: "",
      }),
      t(`events.details.ticket_panel.policy_details.${knownPolicy}.rule_3`, {
        defaultValue: "",
      }),
    ].filter(Boolean),
  };
};

const getCaseStatusTone = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === "resolved") return "success";
  if (normalized === "rejected") return "danger";
  if (normalized === "open" || normalized === "in_review") return "warning";
  return "default";
};

const getCaseStatusLabel = (t, status) =>
  t(`events.details.ticket_panel.case_statuses.${normalizeStatus(status) || "unknown"}`);

const getPaymentHistoryTitle = (t, stage) => {
  const normalized = normalizeStatus(stage);
  if (normalized === "deposit_received") {
    return t("events.details.ticket_panel.payment_history_deposit");
  }
  if (normalized === "remaining_balance_paid") {
    return t("events.details.ticket_panel.payment_history_remaining");
  }
  return t("events.details.ticket_panel.payment_history_ticket");
};

const getFeeBreakdownHint = (t, ticketAmount, feeAmount, currency = "EUR") => {
  const ticket = parseMoney(ticketAmount);
  const fee = parseMoney(feeAmount);
  if (!Number.isFinite(fee) || fee <= 0) return "";
  return t("events.details.ticket_panel.includes_fee_hint", {
    ticket: formatMoney(ticket, currency),
    fee: formatMoney(fee, currency),
  });
};

const NoticePill = ({ label, tone = "default" }) => {
  const toneClass =
    tone === "warning"
      ? "border-amber-300/35 bg-amber-400/15 text-amber-100"
      : tone === "success"
        ? "border-emerald-300/35 bg-emerald-400/15 text-emerald-100"
        : tone === "danger"
          ? "border-rose-300/35 bg-rose-400/15 text-rose-100"
          : "border-white/16 bg-white/10 text-white/82";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] ${toneClass}`}
    >
      {label}
    </span>
  );
};

const InfoCard = ({ label, value, hint = "" }) => (
  <div className="rounded-2xl border border-white/10 bg-black/10 px-4 py-3">
    <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/48">
      {label}
    </div>
    <div className="mt-1 text-xl font-extrabold text-white">{value}</div>
    {hint ? <div className="mt-1 text-sm leading-5 text-white/58">{hint}</div> : null}
  </div>
);

const DetailRow = ({ label, value, emphasize = false }) => (
  <div className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
    <span className="text-sm font-medium text-white/72">{label}</span>
    <span
      className={`text-right ${emphasize ? "text-lg font-extrabold text-white" : "text-sm font-bold text-white"}`}
    >
      {value}
    </span>
  </div>
);

const SectionCard = ({ title, children, action = null }) => (
  <section className="rounded-3xl border border-white/10 bg-black/10 p-4">
    <div className="mb-3 flex items-center justify-between gap-3">
      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/50">
        {title}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const CaseCard = ({ t, title, requestData, kind, currency = "EUR", settlementMode = "event_hold" }) => {
  if (!requestData) return null;
  const statusLabel = getCaseStatusLabel(t, requestData.status);
  const statusTone = getCaseStatusTone(requestData.status);
  const requestedAt = formatDateTime(requestData.requested_at);
  const resolvedAt = formatDateTime(requestData.resolved_at);
  const refundExpected = parseMoney(requestData.estimated_refund_amount);
  const note =
    kind === "cancellation"
      ? String(requestData.resolved_message || "").trim()
      : String(requestData.status_note || "").trim();

  return (
    <SectionCard
      title={title}
      action={<NoticePill label={statusLabel} tone={statusTone} />}
    >
      <div className="space-y-2">
        {requestedAt ? (
          <DetailRow
            label={t("events.details.ticket_panel.requested_at")}
            value={requestedAt}
          />
        ) : null}
        {resolvedAt ? (
          <DetailRow
            label={t("events.details.ticket_panel.resolved_at")}
            value={resolvedAt}
          />
        ) : null}
        {kind === "cancellation" && Number.isFinite(refundExpected) ? (
          <DetailRow
            label={t("events.details.ticket_panel.refund_expected")}
            value={formatMoney(refundExpected, currency)}
            emphasize={refundExpected > 0}
          />
        ) : null}
        {kind === "dispute" && settlementMode !== "direct" ? (
          <DetailRow
            label={t("events.details.ticket_panel.payout_hold")}
            value={requestData.payout_blocking
              ? t("events.details.ticket_panel.payout_hold_yes")
              : t("events.details.ticket_panel.payout_hold_no")}
          />
        ) : null}
      </div>
      {note ? (
        <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm leading-6 text-white/76">
          <div className="mb-1 font-semibold text-white">
            {t("events.details.ticket_panel.case_note_label")}
          </div>
          <p>{note}</p>
        </div>
      ) : null}
      {kind === "cancellation" && requestData.refund_submitted ? (
        <p className="mt-3 text-sm leading-6 text-white/70">
          {t("events.details.ticket_panel.refund_timing_hint")}
        </p>
      ) : null}
    </SectionCard>
  );
};

const PaymentHistoryRow = ({ t, item }) => {
  const paidAt = formatDateTime(item?.paid_at);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-semibold text-white">
            {getPaymentHistoryTitle(t, item?.stage)}
          </div>
          {paidAt ? (
            <div className="mt-1 text-xs font-medium uppercase tracking-[0.12em] text-white/48">
              {t("events.details.ticket_panel.paid_at")} · {paidAt}
            </div>
          ) : null}
          <div className="mt-2 text-sm leading-6 text-white/68">
            {getFeeBreakdownHint(t, item?.ticket_base_amount, item?.service_fee_amount, item?.currency)}
          </div>
        </div>
        <div className="text-right text-base font-extrabold text-white">
          {formatMoney(item?.total_paid_amount, item?.currency)}
        </div>
      </div>
    </div>
  );
};

const ParticipantTicketPanel = ({
  t,
  ticketState,
  settlementMode = "event_hold",
  cancellationQuote = null,
  cancellationLoading = false,
  cancellationSubmitting = false,
  disputeSubmitting = false,
  onPayNow,
  onLoadCancellation,
  onSubmitCancellation,
  onSubmitDispute,
  triggerLabel = "",
  triggerClassName = "",
  hideTrigger = false,
  externalOpenToken = 0,
  externalOpenView = "summary",
  onExternalOpenHandled = NOOP,
  onModalClose = NOOP,
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [modalView, setModalView] = React.useState("summary");
  const [phone, setPhone] = React.useState("");
  const [note, setNote] = React.useState("");
  const [reason, setReason] = React.useState("");

  React.useEffect(() => {
    if (!isModalOpen) {
      setModalView("summary");
      setPhone("");
      setNote("");
      setReason("");
    }
  }, [isModalOpen]);

  React.useEffect(() => {
    if (!externalOpenToken) return;
    const nextView =
      externalOpenView === "cancellation" || externalOpenView === "dispute"
        ? externalOpenView
        : "summary";
    setModalView(nextView);
    setIsModalOpen(true);
    if (nextView === "cancellation" && typeof onLoadCancellation === "function") {
      void onLoadCancellation();
    }
    onExternalOpenHandled();
  }, [externalOpenToken, externalOpenView, onExternalOpenHandled, onLoadCancellation]);

  if (!hasPaidParticipantReservation(ticketState)) return null;

  const currency = ticketState.currency || "EUR";

  const isVaultGridTeamTicket =
    ticketState.ticketing_flow_kind === "vaultgrid_revolut" ||
    ticketState.cancellation_policy === "vaultgrid_team_standard";
  const statusCopy = getStatusCopy(ticketState, t);
  const dueAtLabel = formatDateTime(ticketState.balance_due_at);
  const outstandingAmount = parseMoney(ticketState.outstanding_amount) || 0;
  const hasOutstandingBalance = Number.isFinite(outstandingAmount) && outstandingAmount > 0;
  const canPayNow = Boolean(
    ticketState.can_pay_now &&
      hasOutstandingBalance
  );
  const supportsFullJoinChoice = Boolean(ticketState.full_payment_available_now);
  const supportsDepositJoin =
    !supportsFullJoinChoice &&
    Number(ticketState.join_percent || 0) > 0 &&
    Number(ticketState.join_percent || 0) < 100 &&
    !ticketState.deposit_paid;
  const payLabel = ticketState.balance_due_now
    ? t("events.details.ticket_panel.pay_remaining_now")
    : ticketState.deposit_paid
      ? t("events.details.ticket_panel.pay_remaining_ticket")
      : supportsFullJoinChoice
        ? t("events.details.ticket_panel.join_event")
      : supportsDepositJoin
        ? t("events.details.ticket_panel.join_with_deposit")
      : t("events.details.ticket_panel.pay_ticket_now");

  const openCancellationRequest = ticketState.open_cancellation_request || null;
  const openDispute = ticketState.open_dispute || null;
  const latestCancellationRequest =
    ticketState.latest_cancellation_request ||
    openCancellationRequest ||
    cancellationQuote?.existing_request ||
    null;
  const latestDispute =
    ticketState.latest_dispute ||
    openDispute ||
    cancellationQuote?.open_dispute ||
    null;
  const hasOpenCancellation = Boolean(openCancellationRequest?.id);
  const hasOpenDispute = Boolean(openDispute?.id);
  const activeCancellationRequest = hasOpenCancellation
    ? openCancellationRequest
    : null;
  const activeDispute = hasOpenDispute ? openDispute : null;
  const paymentHistory = Array.isArray(ticketState.payment_history)
    ? ticketState.payment_history.filter(
        (item) => Number.isFinite(parseMoney(item?.total_paid_amount))
      )
    : [];
  const depositPaymentEntry =
    paymentHistory.find(
      (item) => normalizeStatus(item?.stage) === "deposit_received"
    ) || null;
  const remainingPaymentEntry =
    paymentHistory.find(
      (item) => normalizeStatus(item?.stage) === "remaining_balance_paid"
    ) || null;
  const fullPaymentEntry =
    paymentHistory.find((item) => {
      const stage = normalizeStatus(item?.stage);
      return stage !== "deposit_received" && stage !== "remaining_balance_paid";
    }) || null;
  const joinCardAmount =
    depositPaymentEntry?.total_paid_amount ??
    fullPaymentEntry?.total_paid_amount ??
    ticketState.join_checkout_amount ??
    ticketState.join_amount;
  const joinCardHint = depositPaymentEntry
    ? getFeeBreakdownHint(
        t,
        depositPaymentEntry.ticket_base_amount,
        depositPaymentEntry.service_fee_amount,
        currency
      )
    : fullPaymentEntry
      ? getFeeBreakdownHint(
          t,
          fullPaymentEntry.ticket_base_amount,
          fullPaymentEntry.service_fee_amount,
          currency
        )
      : getFeeBreakdownHint(
          t,
          ticketState.join_amount,
          ticketState.deposit_service_fee_amount,
          currency
        );
  const remainingBalanceHint = remainingPaymentEntry
    ? getFeeBreakdownHint(
        t,
        remainingPaymentEntry.ticket_base_amount,
        remainingPaymentEntry.service_fee_amount,
        currency
      )
    : parseMoney(ticketState.remaining_paid_amount) > 0 &&
        !fullPaymentEntry
      ? getFeeBreakdownHint(
          t,
          ticketState.remaining_paid_amount,
          ticketState.remaining_service_fee_amount,
          currency
        )
      : "";
  const policyCopy = getPolicyCopy(t, ticketState.cancellation_policy);
  const existingCancellationRequest = cancellationQuote?.existing_request || activeCancellationRequest;

  const openReservationModal = () => {
    setModalView("summary");
    setIsModalOpen(true);
  };

  const openCancellationView = () => {
    setModalView("cancellation");
    setIsModalOpen(true);
    if (typeof onLoadCancellation === "function") {
      void onLoadCancellation();
    }
  };

  const openDisputeView = () => {
    setModalView("dispute");
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    onModalClose();
  };

  const handleCancellationSubmit = async () => {
    const ok = await onSubmitCancellation?.({ phone, note });
    if (ok?.ok || ok === true) closeModal();
  };

  const handleDisputeSubmit = async () => {
    const ok = await onSubmitDispute?.({ phone, reason });
    if (ok?.ok || ok === true) closeModal();
  };

  const summaryBody = (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.16em] ${statusCopy?.className || "bg-white/12 text-white"}`}
        >
          {statusCopy?.label}
        </span>
        {hasOpenCancellation ? (
          <NoticePill
            label={t("events.details.ticket_panel.cancellation_pending")}
            tone="warning"
          />
        ) : null}
        {hasOpenDispute ? (
          <NoticePill
            label={t("events.details.ticket_panel.dispute_open")}
            tone="warning"
          />
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
        {isVaultGridTeamTicket ? (
          <InfoCard
            label={t("events.details.ticket_panel.ticket_paid")}
            value={formatMoney(ticketState.total_paid_amount, currency)}
          />
        ) : (
          <>
            <InfoCard
              label={t("events.details.ticket_panel.ticket_price")}
              value={formatMoney(ticketState.full_amount, currency)}
            />
            <InfoCard
              label={t("events.details.ticket_panel.ticket_paid")}
              value={formatMoney(ticketState.ticket_paid_amount, currency)}
            />
            {parseMoney(ticketState.service_fee_paid_amount) > 0 ? (
              <InfoCard
                label={t("events.details.ticket_panel.stripe_fees_paid")}
                value={formatMoney(ticketState.service_fee_paid_amount, currency)}
              />
            ) : null}
            <InfoCard
              label={t("events.details.ticket_panel.paid_total")}
              value={formatMoney(ticketState.total_paid_amount, currency)}
              hint={getFeeBreakdownHint(
                t,
                ticketState.ticket_paid_amount,
                ticketState.service_fee_paid_amount,
                currency
              )}
            />
          </>
        )}
        {!isVaultGridTeamTicket ? (
          <>
            <InfoCard
              label={t("events.details.ticket_panel.paid_on_join")}
              value={`${formatMoney(joinCardAmount, currency)}${depositPaymentEntry && ticketState.join_percent < 100 ? ` • ${ticketState.join_percent}%` : ""}`}
              hint={joinCardHint}
            />
            {hasOutstandingBalance ? (
              <InfoCard
                label={t("events.details.ticket_panel.remaining_balance")}
                value={formatMoney(ticketState.outstanding_amount, currency)}
                hint={remainingBalanceHint}
              />
            ) : null}
          </>
        ) : null}
      </div>

      {!isVaultGridTeamTicket && paymentHistory.length > 0 ? (
        <SectionCard title={t("events.details.ticket_panel.payment_history_title")}>
          <div className="space-y-2">
            {paymentHistory.map((item) => (
              <PaymentHistoryRow
                key={item.id || `${item.stage}-${item.paid_at || ""}`}
                t={t}
                item={item}
              />
            ))}
          </div>
        </SectionCard>
      ) : !isVaultGridTeamTicket ? (
        <SectionCard title={t("events.details.ticket_panel.payment_history_title")}>
          <p className="text-sm leading-6 text-white/70">
            {t("events.details.ticket_panel.payment_history_empty")}
          </p>
        </SectionCard>
      ) : null}

      {!isVaultGridTeamTicket && hasOutstandingBalance ? (
        <SectionCard title={t("events.details.ticket_panel.balance_request")}>
          <div className="space-y-2">
            <DetailRow
              label={t("events.details.ticket_panel.balance_deadline_label")}
              value={
                dueAtLabel
                  ? dueAtLabel
                  : ticketState.balance_due_days_before != null
                  ? t("events.details.ticket_panel.balance_request_days", {
                      days: ticketState.balance_due_days_before,
                    })
                  : t("events.details.ticket_panel.balance_request_manual")
              }
            />
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm leading-6 text-white/74">
              {ticketState.balance_due_now ? (
                <p>{t("events.details.ticket_panel.deadline_now")}</p>
              ) : dueAtLabel && ticketState.balance_due_days_before != null ? (
                <p>
                  {t("events.details.ticket_panel.balance_due_at_with_days", {
                    date: dueAtLabel,
                    days: ticketState.balance_due_days_before,
                  })}
                </p>
              ) : dueAtLabel ? (
                <p>
                  {t("events.details.ticket_panel.balance_due_at", {
                    date: dueAtLabel,
                  })}
                </p>
              ) : (
                <p>{t("events.details.ticket_panel.deadline_manual")}</p>
              )}
            </div>
          </div>
        </SectionCard>
      ) : null}

      <SectionCard title={t("events.details.ticket_panel.policy_title")}>
        <div className="space-y-3">
          <div>
            <div className="text-base font-extrabold text-white">{policyCopy.label}</div>
            <p className="mt-1 text-sm leading-6 text-white/74">{policyCopy.summary}</p>
          </div>
          <div className="space-y-2">
            {policyCopy.rules.map((rule) => (
              <div
                key={rule}
                className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm leading-6 text-white/78"
              >
                {rule}
              </div>
            ))}
          </div>
          <p className="text-sm leading-6 text-white/66">
            {t(
              isVaultGridTeamTicket
                ? "events.details.ticket_panel.team_policy_hint"
                : "events.details.ticket_panel.policy_hint"
            )}
          </p>
          {parseMoney(ticketState.service_fee_paid_amount) > 0 ? (
            <p className="text-sm leading-6 text-white/66">
              {t("events.details.ticket_panel.historical_fee_policy_hint")}
            </p>
          ) : null}
          {!isVaultGridTeamTicket ? (
            <p className="text-xs leading-5 text-white/62">
              {t("confirm_intent_modal.body.host_change_refund_note")}
            </p>
          ) : null}
        </div>
      </SectionCard>

      {latestCancellationRequest ? (
        <CaseCard
          t={t}
          title={t("events.details.ticket_panel.cancellation_case_title")}
          requestData={latestCancellationRequest}
          kind="cancellation"
          currency={currency}
        />
      ) : null}

      {!isVaultGridTeamTicket && latestDispute ? (
        <CaseCard
          t={t}
          title={t("events.details.ticket_panel.dispute_case_title")}
          requestData={latestDispute}
          kind="dispute"
          settlementMode={settlementMode}
          currency={currency}
        />
      ) : null}

      <div className="flex flex-col gap-2 md:flex-row">
        {canPayNow ? (
          <button
            type="button"
            onClick={onPayNow}
            className="flex-1 rounded-2xl bg-white px-4 py-3 text-base font-extrabold text-blue-600"
          >
            {payLabel}
          </button>
        ) : null}
        <button
          type="button"
          onClick={openCancellationView}
          className="flex-1 rounded-2xl border border-white/22 bg-white/[0.04] px-4 py-3 text-base font-bold text-white"
        >
          {hasOpenCancellation
            ? t("events.details.ticket_panel.cancellation_pending")
            : latestCancellationRequest
              ? t("events.details.ticket_panel.view_cancellation")
              : t("events.details.ticket_panel.cancellation_action")}
        </button>
        {!isVaultGridTeamTicket ? (
          <button
            type="button"
            onClick={openDisputeView}
            className="flex-1 rounded-2xl border border-white/22 bg-white/[0.04] px-4 py-3 text-base font-bold text-white"
          >
            {hasOpenDispute
              ? t("events.details.ticket_panel.dispute_open")
              : latestDispute
                ? t("events.details.ticket_panel.view_dispute")
                : t("events.details.ticket_panel.dispute")}
          </button>
        ) : null}
      </div>
    </div>
  );

  const cancellationBody = cancellationLoading ? (
    <div className="py-12 text-center text-sm font-medium text-white/72">
      {t("events.details.ticket_panel.cancellation_modal.loading")}
    </div>
  ) : (
    <div className="space-y-4">
      {latestCancellationRequest ? (
        <CaseCard
          t={t}
          title={t("events.details.ticket_panel.cancellation_case_title")}
          requestData={latestCancellationRequest}
          kind="cancellation"
          currency={currency}
        />
      ) : null}

      {latestDispute ? (
        <CaseCard
          t={t}
          title={t("events.details.ticket_panel.dispute_case_title")}
          requestData={latestDispute}
          kind="dispute"
          settlementMode={settlementMode}
          currency={currency}
        />
      ) : null}

      <SectionCard title={t("events.details.ticket_panel.cancellation_modal.breakdown")}>
        <div className="space-y-2">
          <DetailRow
            label={t("events.details.ticket_panel.cancellation_modal.total_paid")}
            value={formatMoney(cancellationQuote?.total_paid_amount, currency)}
          />
          <DetailRow
            label={t("events.details.ticket_panel.ticket_paid")}
            value={formatMoney(cancellationQuote?.refundable_base_amount, currency)}
          />
          {!isVaultGridTeamTicket &&
          (parseMoney(cancellationQuote?.service_fee_retained_amount) || 0) > 0 ? (
            <DetailRow
              label={t("events.details.ticket_panel.cancellation_modal.service_fee")}
              value={`- ${formatMoney(cancellationQuote?.service_fee_retained_amount, currency)}`}
            />
          ) : null}
          <DetailRow
            label={t("events.details.ticket_panel.cancellation_modal.policy_deduction", {
              percent: cancellationQuote?.policy_refund_percent ?? 0,
            })}
            value={`- ${formatMoney(cancellationQuote?.policy_deduction_amount, currency)}`}
          />
          <DetailRow
            label={t("events.details.ticket_panel.cancellation_modal.refund_amount")}
            value={formatMoney(cancellationQuote?.estimated_refund_amount, currency)}
            emphasize={true}
          />
        </div>
      </SectionCard>

      <SectionCard title={t("events.details.ticket_panel.policy_title")}>
        <div className="space-y-2 text-sm leading-6 text-white/74">
          <p className="font-semibold text-white">{policyCopy.label}</p>
          <p>{policyCopy.summary}</p>
          {policyCopy.rules.map((rule) => (
            <div
              key={rule}
              className="rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3"
            >
              {rule}
            </div>
          ))}
          <p>
            {t(
              isVaultGridTeamTicket
                ? "events.details.ticket_panel.cancellation_modal.team_policy_note"
                : "events.details.ticket_panel.cancellation_modal.policy_note"
            )}
          </p>
          {!isVaultGridTeamTicket &&
          parseMoney(cancellationQuote?.service_fee_retained_amount) > 0 ? (
            <p>{t("events.details.ticket_panel.cancellation_modal.historical_fee_policy_note")}</p>
          ) : null}
          {!isVaultGridTeamTicket ? (
            <p className="text-xs leading-5 text-white/62">
              {t("confirm_intent_modal.body.host_change_refund_note")}
            </p>
          ) : null}
        </div>
      </SectionCard>

      {cancellationQuote?.open_dispute ? (
        <div className="rounded-3xl border border-amber-300/30 bg-amber-400/10 p-4 text-sm leading-6 text-amber-50">
          {t("events.details.ticket_panel.cancellation_modal.dispute_block_note")}
        </div>
      ) : null}

      {existingCancellationRequest ? (
        <div className="rounded-3xl border border-white/10 bg-black/10 p-4 text-sm leading-6 text-white/74">
          {t("events.details.ticket_panel.cancellation_modal.request_exists")}
        </div>
      ) : cancellationQuote?.can_submit && !isVaultGridTeamTicket ? (
        <>
          <label className="block">
            <div className="mb-2 text-sm font-semibold text-white">
              {t("events.details.ticket_panel.cancellation_modal.phone_label")}
            </div>
            <input
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder={t("events.details.ticket_panel.cancellation_modal.phone_placeholder")}
              className="h-12 w-full rounded-2xl border border-white/14 bg-black/10 px-4 text-white outline-none placeholder:text-white/36"
            />
          </label>

          <label className="block">
            <div className="mb-2 text-sm font-semibold text-white">
              {t("events.details.ticket_panel.cancellation_modal.note_label")}
            </div>
            <textarea
              rows={4}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t("events.details.ticket_panel.cancellation_modal.note_placeholder")}
              className="w-full rounded-2xl border border-white/14 bg-black/10 px-4 py-3 text-white outline-none placeholder:text-white/36"
            />
          </label>
        </>
      ) : null}
    </div>
  );

  const disputeBody = (
    <div className="space-y-4">
      {latestDispute ? (
        <CaseCard
          t={t}
          title={t("events.details.ticket_panel.dispute_case_title")}
          requestData={latestDispute}
          kind="dispute"
          settlementMode={settlementMode}
          currency={currency}
        />
      ) : null}

      <SectionCard title={t("events.details.ticket_panel.dispute_modal.title")}>
        <div className="space-y-3 text-sm leading-6 text-white/74">
          <p>{t("events.details.ticket_panel.dispute_modal.body_intro")}</p>
          <p>{t(settlementMode === "direct"
            ? "events.details.ticket_panel.dispute_modal.body_direct"
            : "events.details.ticket_panel.dispute_modal.body_hold")}</p>
        </div>
      </SectionCard>

      {!activeDispute ? (
        <>
          <label className="block">
            <div className="mb-2 text-sm font-semibold text-white">
              {t("events.details.ticket_panel.dispute_modal.phone_label")}
            </div>
            <input
              type="tel"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
              placeholder={t("events.details.ticket_panel.dispute_modal.phone_placeholder")}
              className="h-12 w-full rounded-2xl border border-white/14 bg-black/10 px-4 text-white outline-none placeholder:text-white/36"
            />
          </label>

          <label className="block">
            <div className="mb-2 text-sm font-semibold text-white">
              {t("events.details.ticket_panel.dispute_modal.reason_label")}
            </div>
            <textarea
              rows={5}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder={t("events.details.ticket_panel.dispute_modal.reason_placeholder")}
              className="w-full rounded-2xl border border-white/14 bg-black/10 px-4 py-3 text-white outline-none placeholder:text-white/36"
            />
          </label>
        </>
      ) : (
        <div className="rounded-3xl border border-white/10 bg-black/10 p-4 text-sm leading-6 text-white/74">
          {t("events.details.ticket_panel.dispute_modal.request_exists")}
        </div>
      )}
    </div>
  );

  const modalHeaderTitle =
    modalView === "cancellation"
      ? t("events.details.ticket_panel.cancellation_modal.title")
      : modalView === "dispute"
        ? t("events.details.ticket_panel.dispute_modal.title")
        : t("events.details.ticket_panel.title");

  const modalHeaderSubtitle =
    modalView === "cancellation"
      ? t(
          isVaultGridTeamTicket
            ? "events.details.ticket_panel.cancellation_modal.team_subtitle"
            : "events.details.ticket_panel.cancellation_modal.subtitle"
        )
      : modalView === "dispute"
        ? t("events.details.ticket_panel.dispute_modal.subtitle")
        : statusCopy?.summary;

  const modalFooter =
    modalView === "summary" ? null : modalView === "cancellation" ? (
      existingCancellationRequest ? (
        <AppModalButton
          onClick={() => setModalView("summary")}
          className="text-[#2563EB]"
        >
          {t("events.details.ticket_panel.back_action")}
        </AppModalButton>
      ) : cancellationQuote?.can_submit ? (
        <div className="space-y-3">
          <AppModalButton
            onClick={handleCancellationSubmit}
            disabled={cancellationSubmitting || cancellationLoading}
            loading={cancellationSubmitting}
            className="text-[#2563EB]"
          >
            {cancellationSubmitting
              ? t("events.details.ticket_panel.cancellation_modal.submitting")
              : isVaultGridTeamTicket
                ? (parseMoney(cancellationQuote?.estimated_refund_amount) || 0) > 0
                  ? t(
                      "events.details.ticket_panel.cancellation_modal.team_submit_refund",
                      {
                        amount: formatMoney(
                          cancellationQuote?.estimated_refund_amount
                        ),
                      }
                    )
                  : t(
                      "events.details.ticket_panel.cancellation_modal.team_submit_no_refund"
                    )
                : t("events.details.ticket_panel.cancellation_modal.submit")}
          </AppModalButton>
          <AppModalButton
            variant="secondary"
            onClick={() => setModalView("summary")}
          >
            {t("events.details.ticket_panel.back_action")}
          </AppModalButton>
        </div>
      ) : (
        <AppModalButton
          onClick={() => setModalView("summary")}
          className="text-[#2563EB]"
        >
          {t("events.details.ticket_panel.back_action")}
        </AppModalButton>
      )
    ) : activeDispute ? (
      <AppModalButton
        onClick={() => setModalView("summary")}
        className="text-[#2563EB]"
      >
        {t("events.details.ticket_panel.back_action")}
      </AppModalButton>
    ) : (
      <div className="space-y-3">
        <AppModalButton
          onClick={handleDisputeSubmit}
          disabled={disputeSubmitting}
          loading={disputeSubmitting}
          className="text-[#2563EB]"
        >
          {disputeSubmitting
            ? t("events.details.ticket_panel.dispute_modal.submitting")
            : t("events.details.ticket_panel.dispute_modal.submit")}
        </AppModalButton>
        <AppModalButton
          variant="secondary"
          onClick={() => setModalView("summary")}
        >
          {t("events.details.ticket_panel.back_action")}
        </AppModalButton>
      </div>
    );

  const resolvedTriggerClassName =
    triggerClassName ||
    "w-full h-12 rounded-2xl bg-white text-lg font-semibold text-blue-600";

  return (
    <>
      {!hideTrigger ? (
        <button
          type="button"
          onClick={openReservationModal}
          className={resolvedTriggerClassName}
        >
          {triggerLabel || t("events.details.ticket_panel.cta_label")}
        </button>
      ) : null}

      {isModalOpen ? (
        <AppModalShell
          open={isModalOpen}
          title={modalHeaderTitle}
          description={modalHeaderSubtitle}
          onClose={closeModal}
          closeAriaLabel={t("events.details.ticket_panel.modal_close")}
          maxWidthClassName="max-w-[52rem]"
          descriptionClassName="text-center"
          footer={modalFooter}
        >
          {modalView === "summary"
            ? summaryBody
            : modalView === "cancellation"
              ? cancellationBody
              : disputeBody}
        </AppModalShell>
      ) : null}
    </>
  );
};

export default ParticipantTicketPanel;
